import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  MapPin, Navigation, CalendarCheck, Star, Camera,
  Building2, Stethoscope, Heart, Eye, ArrowRight, Search, UserPlus, X, Share2
} from 'lucide-react';
import InviteYourDoctorQR from '@/components/InviteYourDoctorQR';
import { trackPreviewEvent } from '@/lib/analytics';
import bannerForDoctor from '@/lib/specialtyBanner';
import groupBySpecialty from '@/lib/groupBySpecialty';
import { buildRecommendUrl } from '@/lib/recommendShare';

// Public preview of the patient "My Clinics" dashboard.
// Mirrors /my-clinics with realistic mock data so a not-yet-registered
// patient can see exactly what they'll get after signing up. Linked from
// /for-patients and shareable to WhatsApp groups.
const SAMPLE_CLINICS = [
  {
    id: 'sample-rajesh',
    doctor_name: 'Dr. Rajesh Sharma',
    specializations: ['Cardiology', 'Internal Medicine'],
    clinic_name: 'Sharma Heart Clinic',
    address: 'Plot 14, Linking Road',
    city: 'Bandra West, Mumbai',
    pincode: '400050',
    is_favorite: true,
    slug: 'dr-rajesh-sharma',
  },
  {
    id: 'sample-anjali',
    doctor_name: 'Dr. Anjali Mehta',
    specializations: ['Dermatology', 'Cosmetology'],
    clinic_name: 'Skin & Wellness Studio',
    address: 'Ground Floor, Crystal Plaza',
    city: 'Andheri West, Mumbai',
    pincode: '400053',
    is_favorite: false,
    slug: 'dr-rajesh-sharma',
  },
  {
    id: 'sample-vikram',
    doctor_name: 'Dr. Vikram Iyer',
    specializations: ['ENT Surgery', 'Audiology'],
    clinic_name: 'Iyer ENT Centre',
    address: '2nd Floor, Galaxy Towers',
    city: 'Powai, Mumbai',
    pincode: '400076',
    is_favorite: false,
    slug: 'dr-rajesh-sharma',
  },
  {
    id: 'sample-meera',
    doctor_name: 'Dr. Meera Kapoor',
    specializations: ['ENT Surgery', 'Pediatric ENT'],
    clinic_name: 'Kapoor ENT & Hearing',
    address: 'Shop 4, Hiranandani Arcade',
    city: 'Powai, Mumbai',
    pincode: '400076',
    is_favorite: false,
    slug: 'dr-rajesh-sharma',
  },
];

const directionsUrl = (c) => {
  const parts = [c.clinic_name, c.address, c.city, c.pincode].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts)}`;
};

export default function MyClinicsPreviewPage() {
  // Sticky footer banner — slides up after the user scrolls past the first viewport,
  // dismissible via the close (×) button. Stored in sessionStorage so it doesn't
  // immediately reappear on the same visit.
  const [showSticky, setShowSticky] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Fire on first mount only — the helper de-dupes anyway.
    trackPreviewEvent('preview_view');
    if (typeof window === 'undefined') return;
    if (sessionStorage.getItem('preview-sticky-dismissed') === '1') {
      setDismissed(true);
      return;
    }
    let firedShown = false;
    const onScroll = () => {
      const visible = window.scrollY > 300;
      setShowSticky(visible);
      if (visible && !firedShown) {
        firedShown = true;
        trackPreviewEvent('preview_sticky_shown');
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const dismissSticky = () => {
    setDismissed(true);
    trackPreviewEvent('preview_sticky_dismiss');
    try { sessionStorage.setItem('preview-sticky-dismissed', '1'); } catch (_) {}
  };

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="my-clinics-preview-page">
      <Helmet>
        <title>Preview My Trusted Medical Clinics — DocSite Patient Dashboard</title>
        <meta name="description" content="A public preview of the DocSite patient dashboard. See what your saved clinics, QR scans, and one-tap doctor invitations look like before you sign up." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Preview banner */}
      <div className="bg-gradient-to-r from-amber-50 via-white to-amber-50 border-b border-amber-200" data-testid="preview-banner">
        <div className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-amber-800">
            <Eye size={14} />
            <p className="text-xs font-semibold uppercase tracking-[0.18em]">Preview mode — sample data, no login required</p>
          </div>
          <Link
            to="/register?as=patient"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-amber-600 text-white text-xs font-bold hover:bg-amber-700 transition-colors"
            data-testid="preview-banner-cta"
          >
            Sign up to make it yours <ArrowRight size={12} />
          </Link>
        </div>
      </div>

      <section className="w-full px-6 md:px-12 lg:px-20 xl:px-28 py-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-3">
              <Heart size={12} fill="currentColor" /> Hi Priya
            </div>
            <h1 className="text-3xl md:text-4xl font-medium text-slate-900 leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              My Trusted Medical Clinics
            </h1>
            <p className="text-sm text-slate-600 mt-2 max-w-xl">
              Every doctor whose QR you scan appears here. Tap to book again, get directions, or favourite it.
            </p>
          </div>
          <button
            type="button"
            disabled
            title="Sign up to scan a real QR"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-sky-600/60 text-white text-sm font-semibold cursor-not-allowed"
            data-testid="preview-scan-btn"
          >
            <Camera size={14} /> Scan Doctor’s QR
          </button>
        </div>

        {/* Sample clinic grid — grouped by specialty */}
        <div className="space-y-6 mb-8" data-testid="preview-clinics-grid">
          {groupBySpecialty(SAMPLE_CLINICS).map((group) => {
            const banner = bannerForDoctor([group.slug]);
            return (
              <section key={group.slug} className="rounded-3xl border border-slate-200 bg-white overflow-hidden" data-testid={`preview-clinic-group-${group.slug}`}>
                {banner && (
                  <div className="relative w-full h-28 sm:h-32 bg-slate-900 overflow-hidden">
                    <img src={banner} alt="" loading="lazy" decoding="async"
                         width="1024" height="576"
                         className="absolute inset-0 w-full h-full object-cover object-center opacity-95"
                         onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900/65 via-slate-900/15 to-transparent" />
                    <div className="absolute inset-0 flex items-end justify-between px-4 sm:px-5 pb-3">
                      <div className="text-white drop-shadow">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/75">Your trusted specialty</p>
                        <h3 className="text-base sm:text-lg font-semibold leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{group.label}</h3>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <a
                          href={buildRecommendUrl({ specialtyLabel: group.label, doctors: group.doctors, slugAccessor: (c) => c.slug })}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackPreviewEvent('preview_recommend_specialty', { specialty: group.slug })}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-white text-[10px] font-bold shadow-sm hover:bg-emerald-600 transition-colors"
                          data-testid={`preview-recommend-${group.slug}`}
                          title={`Recommend ${group.doctors.length === 1 ? 'this doctor' : 'these doctors'} to a friend on WhatsApp`}
                        >
                          <Share2 size={11} /> Recommend to friend
                        </a>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur text-[10px] font-semibold text-white/95">
                          {group.doctors.length} doctor{group.doctors.length === 1 ? '' : 's'}
                        </span>
                      </div>
                    </div>
                    <span className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-900/55 backdrop-blur text-[9px] font-semibold tracking-[0.12em] text-white/90 uppercase">
                      DocSite<sup className="ml-0.5 text-[7px]">™</sup>
                    </span>
                  </div>
                )}

                <div
                  className="grid gap-3 p-4"
                  style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}
                  data-testid={`preview-clinic-tiles-${group.slug}`}
                >
                {group.doctors.map((c) => (
                  <article key={c.id} className="rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md hover:border-sky-200 transition-all" data-testid={`preview-clinic-card-${c.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center flex-shrink-0">
                          <Stethoscope size={14} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[13px] font-semibold text-slate-900 truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{c.doctor_name}</p>
                          <p className="text-[10px] text-slate-500 truncate">{c.specializations.slice(0, 2).join(' · ')}</p>
                        </div>
                      </div>
                      <span aria-label={c.is_favorite ? 'Favourite' : ''}
                            className={`p-1 rounded-lg ${c.is_favorite ? 'text-amber-500' : 'text-slate-300'}`}>
                        <Star size={13} fill={c.is_favorite ? 'currentColor' : 'none'} />
                      </span>
                    </div>

                    <div className="flex items-start gap-1.5 text-[11px] text-slate-600 leading-snug min-h-[36px]">
                      <Building2 size={11} className="mt-0.5 flex-shrink-0 text-slate-400" />
                      <div className="min-w-0">
                        <p className="font-medium text-slate-800 truncate">{c.clinic_name}</p>
                        <p className="truncate">{[c.address, c.city, c.pincode].filter(Boolean).join(', ')}</p>
                      </div>
                    </div>

                    <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-1.5">
                      <Link to={`/dr/${c.slug}`}
                            className="flex-1 min-w-[88px] inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 text-white text-[11px] font-semibold hover:bg-slate-800 transition-colors"
                            data-testid={`preview-book-${c.id}`}>
                        <CalendarCheck size={11} /> Book
                      </Link>
                      <a href={directionsUrl(c)} target="_blank" rel="noopener noreferrer"
                         className="flex-1 min-w-[88px] inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-sky-600 text-white text-[11px] font-semibold hover:bg-sky-700 transition-colors"
                         data-testid={`preview-directions-${c.id}`}>
                        <Navigation size={11} /> Directions
                      </a>
                    </div>
                  </article>
                ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* Invite QR */}
        <InviteYourDoctorQR defaultName="Priya" />

        {/* Browse-more strip */}
        <div className="mt-8 flex items-center justify-center gap-4 flex-wrap">
          <Link to="/insights" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-sky-600" data-testid="preview-browse-websites">
            <Search size={13} /> Browse Doctor Websites near you, powered by DocSite
          </Link>
          <span className="text-slate-300">·</span>
          <Link to="/for" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-sky-600" data-testid="preview-find-more">
            <MapPin size={13} /> Find another specialist in your city
          </Link>
        </div>

        {/* Closing CTA */}
        <div className="mt-12 rounded-3xl bg-gradient-to-br from-sky-600 to-sky-700 text-white p-8 md:p-10 text-center" data-testid="preview-bottom-cta">
          <p className="text-[10px] font-bold text-sky-200 uppercase tracking-[0.2em] mb-3">Ready for the real thing?</p>
          <h2 className="text-2xl md:text-3xl font-medium leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Save your real doctors. One tap to book. Forever free.
          </h2>
          <p className="text-sm text-sky-100/80 mt-3 max-w-xl mx-auto leading-relaxed">
            Sign up takes 30 seconds — no credit card, no spam. Your saved clinics work offline. Your doctor stays in control.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Link to="/register?as=patient" onClick={() => trackPreviewEvent('preview_bottom_cta')} className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-sky-700 font-bold text-sm hover:bg-sky-50 transition-colors" data-testid="preview-register-cta">
              Create my free account <ArrowRight size={14} />
            </Link>
            <Link to="/invite-your-doctor" onClick={() => trackPreviewEvent('preview_invite_click')} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-800/40 border border-white/20 text-white font-medium text-sm hover:bg-sky-800/60 transition-colors" data-testid="preview-invite-cta">
              <UserPlus size={14} /> Invite your doctor first
            </Link>
          </div>
        </div>
      </section>

      {/* Sticky bottom banner — slides up after user scrolls past hero, dismissible per session.
          Adds bottom padding so the page CTAs don't get covered. */}
      {!dismissed && showSticky && <div aria-hidden="true" className="h-14" />}
      {!dismissed && (
        <div
          className={`fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ${showSticky ? 'translate-y-0' : 'translate-y-full'}`}
          data-testid="preview-sticky-banner"
        >
          <div className="bg-gradient-to-r from-emerald-600 via-emerald-500 to-emerald-600 text-white shadow-lg shadow-emerald-900/20">
            <div className="max-w-5xl mx-auto px-4 md:px-12 py-3 flex items-center justify-between gap-3">
              <p className="text-xs sm:text-sm font-medium leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <span className="hidden sm:inline">Like what you see? </span>
                <span className="font-bold">Sign up free in 30 seconds.</span>
              </p>
              <div className="flex items-center gap-1.5">
                <Link
                  to="/register?as=patient"
                  onClick={() => trackPreviewEvent('preview_sticky_cta')}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white text-emerald-700 text-xs font-bold hover:bg-emerald-50 transition-colors"
                  data-testid="preview-sticky-cta"
                >
                  Try it free <ArrowRight size={12} />
                </Link>
                <button
                  type="button"
                  onClick={dismissSticky}
                  aria-label="Dismiss"
                  className="p-1.5 rounded-full text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                  data-testid="preview-sticky-dismiss"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
